export type BriefPipeline = 'partnerships' | 'cg_deals';
export interface BriefDealRow {
  id: string;
  name: string;
  why: string;
  deadline: string | null;
  last_touch_on: string | null;
  last_touch_kind: string | null;
  next_action_date: string | null;
  next_line: string;
  owner_first_name: string | null;
}
export interface BriefWatchRow {
  id: string;
  name: string;
  why: string;
}
export interface BriefPipelinePayload {
  counts: { red: number; yellow: number; green: number };
  reds: BriefDealRow[];
  red_more: number;
  watch: BriefWatchRow[];
  orders_at_risk?: { order_no: string; client: string; target_ship_date: string }[];
}
export interface BriefDealsPayload {
  partnerships: BriefPipelinePayload;
  cg_deals: BriefPipelinePayload;
}

export function pipelineLabel(pipeline: BriefPipeline): string {
  return pipeline === 'partnerships' ? 'Partnerships' : 'Corporate Gift';
}
export function displayDealRows(rows: readonly BriefDealRow[]): BriefDealRow[] {
  return rows.slice(0, 5);
}
export function moreLabel(count: number): string {
  return count > 0 ? `+${count} more on the board` : '';
}

function string(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}
function count(value: unknown): { red: number; yellow: number; green: number } {
  const row = value as Record<string, unknown>;
  return {
    red: typeof row?.red === 'number' ? row.red : 0,
    yellow: typeof row?.yellow === 'number' ? row.yellow : 0,
    green: typeof row?.green === 'number' ? row.green : 0,
  };
}
function row(value: unknown): BriefDealRow | null {
  const v = value as Record<string, unknown>;
  const id = string(v?.id);
  const name = string(v?.name);
  const why = string(v?.why);
  if (!id || !name || !why) return null;
  return {
    id,
    name,
    why,
    deadline: string(v.deadline),
    last_touch_on: string(v.last_touch_on),
    last_touch_kind: string(v.last_touch_kind),
    next_action_date: string(v.next_action_date),
    next_line: string(v.next_line) ?? '',
    owner_first_name: string(v.owner_first_name),
  };
}
function watch(value: unknown): BriefWatchRow | null {
  const v = value as Record<string, unknown>;
  const id = string(v?.id);
  const name = string(v?.name);
  const why = string(v?.why);
  return id && name && why ? { id, name, why } : null;
}
function pipeline(value: unknown, orders = false): BriefPipelinePayload {
  const v = value as Record<string, unknown>;
  return {
    counts: count(v?.counts),
    reds: Array.isArray(v?.reds)
      ? v.reds.map(row).filter((x): x is BriefDealRow => x !== null)
      : [],
    red_more: typeof v?.red_more === 'number' ? v.red_more : 0,
    watch: Array.isArray(v?.watch)
      ? v.watch.map(watch).filter((x): x is BriefWatchRow => x !== null)
      : [],
    ...(orders
      ? {
          orders_at_risk: Array.isArray(v?.orders_at_risk)
            ? v.orders_at_risk.filter(
                (x): x is { order_no: string; client: string; target_ship_date: string } =>
                  typeof (x as Record<string, unknown>)?.order_no === 'string' &&
                  typeof (x as Record<string, unknown>)?.client === 'string' &&
                  typeof (x as Record<string, unknown>)?.target_ship_date === 'string',
              )
            : [],
        }
      : {}),
  };
}
export function portalDealsFrom(value: unknown): BriefDealsPayload {
  const v = value as Record<string, unknown>;
  return { partnerships: pipeline(v?.partnerships), cg_deals: pipeline(v?.cg_deals, true) };
}
